import QUERY_KEY from "@/constants/queryKey";
import { getTeamData } from "@/servers/apis/team";
import { Box } from "@chakra-ui/react";
import {
  colors,
  HStack,
  GoiLogo,
  NextLink,
  customEvent,
  responsive,
  VStack,
  Spacing,
  TabBar,
  useResponsive,
} from "goi_common";
import { useRouter } from "next/router";
import { useQuery } from "react-query";
import { useEffect, useMemo } from "react";

export default function B2BOpsHeader() {
  const router = useRouter();
  const { id, login_pass } = router.query;
  const isEm = router.pathname.includes("em");
  const isLoginPass = login_pass === "true";

  const { data: teamData, isLoading } = useQuery(
    QUERY_KEY.TEAM_DATA,
    () => getTeamData(id as string),
    { enabled: !!id && !isLoginPass }
  );

  useEffect(() => {
    sessionStorage.setItem("company_name", teamData?.company_name || "");
  }, [teamData]);

  if (isLoading || !teamData) return null;

  return (
    <>
      <VStack
        css={responsive({
          height: {
            base: 82,
            lg: 66,
          },
          borderBottom: `1px solid ${colors.gray100}`,
          alignItems: "center",
          justifyContent: { base: "space-between", lg: "center" },
          padding: { base: "10px 10px 0", lg: "0 16px" },
          margin: "0 auto",
          position: "fixed",
          width: "100%",
          top: "0",
          backgroundColor: colors.white,
          transition: "background-color 0.1s linear",
          zIndex: "1000",
          userSelect: "none",
        })}
      >
        <HStack
          alignItems="center"
          gap={"10px"}
          maxWidth="1200px"
          width={"100%"}
        >
          <HStack
            gap="36px"
            css={responsive({
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <NextLink
              id="goi-logo"
              href={isEm ? `/admin/company/em/${id}/` : `/admin/company/${id}/`}
              css={{ width: "100%" }}
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "gnb",
                    trigger_and_target: "logo-uv_home",
                  },
                });
              }}
            >
              <HStack gap={"12px"} alignItems="center">
                <GoiLogo color={"black"} size={30} />
                <div className={"subtitle_21_b black"}>BIZ</div>
                <Box className="subtitle_16_sb">{teamData?.company_name}</Box>
              </HStack>
            </NextLink>

            {!isEm && <HeaderTabBar className="lg_under_hidden" />}
          </HStack>
        </HStack>
        {!isEm && <HeaderTabBar className="lg_over_hidden" />}
      </VStack>
      <Spacing gap={66} className="lg_under_hidden" />
      <Spacing gap={82} className="lg_over_hidden" />
    </>
  );
}

function HeaderTabBar({ className }: { className: string }) {
  const router = useRouter();
  const { activePath } = useActivePathCheck();
  const { isBiggerThanLg } = useResponsive();

  const menu: Array<{
    text: string;
    to: string;
    eventDetails: any;
    onClick?: () => void;
  } | null> = [
    {
      text: "홈",
      to: `/admin/company/${router.query.id}/`,
      eventDetails: ["uv_home"],
    },

    {
      text: "화환발주",
      to: `/admin/company/${router.query.id}/send-wreath`,
      eventDetails: ["uv_funeral"],
    },
    {
      text: "용품발주",
      to: `/admin/company/${router.query.id}/send-disposal`,
      eventDetails: ["uv_disposal"],
    },
    {
      text: "주문내역",
      to: `/admin/company/${router.query.id}/send-history`,
      eventDetails: ["uv_flower"],
    },
  ];

  return (
    <HStack
      css={responsive({
        height: { base: "39px", lg: "100%" },
        marginLeft: { base: "0", lg: "40px" },
        justifyContent: { base: "space-between" },
        alignItems: { base: "flex-end", lg: "center" },
        width: { base: "100%", lg: "970px" },
      })}
      gap={"10px"}
      className={className}
    >
      {menu.map((item, i) => {
        if (!item) return null;
        return (
          <Box position="relative" key={i}>
            <NextLink href={item.to}>
              <TabBar.Tab
                className={"subtitle_18_sb gray500"}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  }
                  item.eventDetails.map((trigger_and_target: any) => {
                    customEvent({
                      newGtm: {
                        path: "gnb",
                        trigger_and_target,
                      },
                    });
                  });
                }}
                isActive={activePath === item.text}
                activeFontStyle={{
                  color: colors.gray900,
                }}
                activeBarStyle={{
                  borderBottom: isBiggerThanLg ? "none" : "2px solid black",
                }}
                css={responsive({
                  height: { base: "33px", lg: "fit-content" },
                })}
              >
                {item.text}
              </TabBar.Tab>
            </NextLink>
          </Box>
        );
      })}
    </HStack>
  );
}

export function useActivePathCheck() {
  const router = useRouter();

  const activePath = useMemo(() => {
    const path = router.asPath;
    if (path.startsWith(`/admin/company/${router.query.id}/send-wreath`))
      return "화환발주";
    else if (path.startsWith(`/admin/company/${router.query.id}/send-disposal`))
      return "용품발주";
    else if (path.startsWith(`/admin/company/${router.query.id}/send-history`))
      return "주문내역";
    else if (
      path.startsWith(`/admin/company/${router.query.id}/funeral-service`)
    )
      return "장례 상담 / 임종 접수";
    else if (path.startsWith(`/admin/company/${router.query.id}`)) return "홈";
    return "";
  }, [router]);

  return { activePath };
}
